import { createApi } from "@reduxjs/toolkit/dist/query/react";
import camelcaseKeys from "camelcase-keys";
import { baseQueryWithProvider } from "@/redux/baseQuery";
import { GetVehicleTypesResponse, VehicleType } from "@/redux/slices/vehicle/types";
import { createSearchParams } from "@/utils";
import { vehicleApi } from "../../../vehicle/vehicleApi";
import { transformGetVehicleTypesResponse } from "./helpers";
import { AddVehicleTypeParams, GetVehicleTypesParams, RawVehicleType, ToggleVehicleTypeStatusParams } from "./types";

export const vehicleTypeApi = createApi({
  baseQuery: baseQueryWithProvider("vehicle/category"),
  reducerPath: "vehicleTypeApi",
  endpoints: (builder) => ({
    getVehicleType: builder.query<VehicleType, string>({
      query: (id) => `/${id}`,
      transformResponse: (res: RawVehicleType) => camelcaseKeys(res, { deep: true }),
      providesTags: (_, __, id) => [{ type: "VehicleType", id }],
    }),
    getVehicleTypes: builder.query<GetVehicleTypesResponse, GetVehicleTypesParams>({
      query: (params) => ({
        url: `?${createSearchParams(params).toString()}`,
      }),
      providesTags: (result) => (result ? result.vehicleTypes.map(({ uuid }) => ({ type: "VehicleType", uuid })) : ["VehicleType"]),
      transformResponse: transformGetVehicleTypesResponse,
    }),
    reorderVehicleTypes: builder.mutation<Record<string, number>, Record<string, number>>({
      query: (body) => ({
        url: "/reorder",
        method: "POST",
        body,
      }),
    }),
    toggleVehicleType: builder.mutation<VehicleType, ToggleVehicleTypeStatusParams>({
      query: ({ id, active }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: { is_active: active },
      }),
    }),
    deleteVehicleType: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(vehicleApi.util.invalidateTags(["VehicleFormOptions"]));
        } catch {
          // mutation failed; nothing to invalidate
        }
      },
    }),
    addVehicleType: builder.mutation<VehicleType, AddVehicleTypeParams>({
      query: (body) => ({
        url: "",
        method: "POST",
        body,
      }),
      transformResponse: (res: RawVehicleType) => camelcaseKeys(res, { deep: true }),
      invalidatesTags: ["VehicleType"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(vehicleApi.util.invalidateTags(["VehicleFormOptions"]));
        } catch {
          // mutation failed; nothing to invalidate
        }
      },
    }),
    updateVehicleType: builder.mutation<VehicleType, Partial<AddVehicleTypeParams> & { id: string }>({
      query: ({ id, ...body }) => ({
        url: `/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (res: RawVehicleType) => camelcaseKeys(res, { deep: true }),
      invalidatesTags: ["VehicleType"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(vehicleApi.util.invalidateTags(["VehicleFormOptions"]));
        } catch {
          // mutation failed; nothing to invalidate
        }
      },
    }),
  }),
  tagTypes: ["VehicleType"],
});

export const {
  useGetVehicleTypeQuery,
  useGetVehicleTypesQuery,
  useLazyGetVehicleTypesQuery,
  useReorderVehicleTypesMutation,
  useToggleVehicleTypeMutation,
  useDeleteVehicleTypeMutation,
  useAddVehicleTypeMutation,
  useUpdateVehicleTypeMutation,
} = vehicleTypeApi;
